import {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useLocation, useNavigate} from 'react-router-dom';
import api from '../../api/client';
import {openTourSearch} from '../Header/tourSearch';
import cardStyles from './CategoryCardList.module.scss';
import s from './CategorySearch.module.scss';

const CategorySearch = () => {
    const [open, setOpen] = useState(false);
    const [keyword, setKeyword] = useState('');
    const [tags, setTags] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const container = useRef(null);
    const trigger = useRef(null);
    const input = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        setKeyword(new URLSearchParams(location.search).get('kw') || '');
        setOpen(false);
    }, [location.search, location.pathname]);

    useEffect(() => {
        if (!open) return;
        let active = true;
        setError(false);
        api.get('/api/tags')
            .then(response => {
                if (active) setTags(response.data || []);
            })
            .catch(() => {
                if (active) setError(true);
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [open]);

    useEffect(() => {
        if (!open || loading) return;
        const triggerElement = trigger.current;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        input.current?.focus();

        const handleOutsideClick = event => {
            if (!container.current?.contains(event.target)) setOpen(false);
        };
        const handleEscape = event => {
            if (event.key === 'Escape') {
                setOpen(false);
                trigger.current?.focus();
            }
            if (event.key === 'Tab') {
                const controls = container.current?.querySelectorAll('input, button');
                if (!controls?.length) return;
                const first = controls[0];
                const last = controls[controls.length - 1];
                if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault();
                    last.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault();
                    first.focus();
                }
            }
        };
        const handleResize = () => {
            if (window.matchMedia('(max-width: 991px)').matches) setOpen(false);
        };
        document.addEventListener('pointerdown', handleOutsideClick);
        document.addEventListener('keydown', handleEscape);
        window.addEventListener('resize', handleResize);
        return () => {
            document.body.style.overflow = previousOverflow;
            triggerElement?.focus();
            document.removeEventListener('pointerdown', handleOutsideClick);
            document.removeEventListener('keydown', handleEscape);
            window.removeEventListener('resize', handleResize);
        };
    }, [open, loading]);

    const handleSubmit = event => {
        event.preventDefault();
        const params = new URLSearchParams();
        if (keyword.trim()) params.set('kw', keyword.trim());
        if (selectedTags.length) params.set('tags', selectedTags.join(','));
        params.set('page', '1');
        setOpen(false);
        navigate({pathname: '/all-tours', search: params.toString()});
    };

    return (
        <div className={`${cardStyles.item} ${s.root}`}>
            <button
                ref={trigger}
                type="button"
                className={`${cardStyles.searchButton} ${s.trigger}`}
                aria-label="ツアーを検索"
                aria-expanded={open && !loading}
                aria-busy={open && loading}
                aria-haspopup="dialog"
                aria-controls={open && !loading ? 'category-search-popup' : undefined}
                onClick={() => {
                    if (window.matchMedia('(max-width: 991px)').matches) {
                        openTourSearch();
                    } else {
                        if (!open) setLoading(true);
                        setOpen(previous => !previous);
                    }
                }}
            >
                <i className={open && loading ? 'fa fa-spinner fa-spin' : 'fa fa-search'} aria-hidden="true"/>
                <span>ツアーを検索</span>
            </button>
            {open && !loading && createPortal(
                <div className={s.backdrop}>
                <form ref={container} id="category-search-popup" className={s.popup} role="dialog" aria-modal="true" aria-label="ツアーを検索" onSubmit={handleSubmit}>
                    <div className={s.inputRow}>
                        <i className="fa fa-search" aria-hidden="true"/>
                        <input
                            ref={input}
                            type="search"
                            aria-label="ツアーを検索"
                            placeholder="ツアーを検索"
                            value={keyword}
                            onChange={event => setKeyword(event.target.value)}
                        />
                        <button type="button" aria-label="Close search" onClick={() => {
                            setOpen(false);
                            trigger.current?.focus();
                        }}>
                            <i className="fa fa-xmark" aria-hidden="true"/>
                        </button>
                    </div>
                    <div className={s.tagHeader}>
                        <strong>ツアーを絞り込む</strong>
                        {selectedTags.length > 0 && (
                            <button type="button" onClick={() => setSelectedTags([])}>すべてクリア</button>
                        )}
                    </div>
                    <div className={s.tags}>
                        {loading && <span role="status">読み込み中...</span>}
                        {error && <span role="alert">タグを読み込めませんでした。キーワードで検索できます。</span>}
                        {!loading && !error && tags.map(tag => (
                            <button
                                key={tag.id}
                                type="button"
                                aria-pressed={selectedTags.includes(tag.name)}
                                className={selectedTags.includes(tag.name) ? s.selected : ''}
                                onClick={() => setSelectedTags(previous => previous.includes(tag.name)
                                    ? previous.filter(name => name !== tag.name)
                                    : [...previous, tag.name])}
                            >
                                {selectedTags.includes(tag.name) && <i className="fa fa-check" aria-hidden="true"/>}
                                {tag.name}
                            </button>
                        ))}
                    </div>
                    <div className={s.footer}>
                        <span>{selectedTags.length > 0 ? `${selectedTags.length} 件のタグを選択中` : 'タグを選択してください'}</span>
                        <button type="submit">検索 <i className="fa fa-arrow-right" aria-hidden="true"/></button>
                    </div>
                </form>
                </div>,
                document.body
            )}
        </div>
    );
};

export default CategorySearch;
